/**
 * Lending Configuration Admin Page
 *
 * Per-tenant lending parameter configuration for platform administrators.
 * Sections: Interest Rates, LTV Thresholds, Loan Parameters, Asset Haircuts.
 *
 * Fetches current config via GET /api/v1/lending-config, submits changes via
 * PUT /api/v1/lending-config. "Load Defaults" fetches baseline values from
 * GET /api/v1/lending-config/defaults. A tenant with no saved configuration
 * has none (GH #2459): the page says so, and the form opens only once the
 * operator loads the defaults to review and save.
 *
 * Type: LendingConfiguration from @aaimplatform/institutional-types/lending-platform-plugins
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Save,
  RotateCcw,
  AlertCircle,
  CheckCircle,
  DollarSign,
  Calendar,
} from 'lucide-react';
import { Alert, Button, Card, Heading, Input, PageHeader, Skeleton } from '@aaimplatform/ui';
import {
  APEX_TENANT_ID,
  isLendingConfigurationIncomplete,
  isLendingConfigurationMissing,
  LendingConfigRequestError,
  lendingConfigTenantId,
  type LendingConfiguration,
} from '../../api/services/lending-config';
import { useAuthStore } from '../../stores/auth';
import {
  useLendingConfig,
  useLendingConfigDefaults,
  useUpdateLendingConfig,
} from '../../hooks/useLendingConfig';

// ---------------------------------------------------------------------------
// Asset Class Display Labels
// ---------------------------------------------------------------------------

// Canonical AssetClass(20) display labels for the operatorConfig context, from
// the terminology register (.claude/manifests/terminology/terms/collateral.yaml,
// term `asset-class`). The map used to be a literal here; two other apps had
// their own, and nothing made the three agree or kept them covering the enum.
// Lookup still falls back to the raw code for any unmapped value.
import { ASSET_CLASS_LABELS } from '../../generated/terminology-labels';
import { percentInputToRatio, ratioToPercentInput } from './config-format';
import { formatMoney, formatRatioAsPercent } from '../../utils/display-format';
import { ConfigSection } from './SettingRows';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * The display label for a haircut key.
 *
 * lending-platform keys haircuts by its own set (EQUITY, FIXED_INCOME,
 * MUTUAL_FUND, ETF, OPTION, FUTURE, CASH, OTHER), and only CASH and OTHER are
 * members of the canonical AssetClass the terminology register labels. Whether
 * the haircut set moves to AssetClass is an open operator question; until it is
 * ruled, an unregistered key is shown in words (EQUITY -> "Equity") rather than
 * as a raw code, and no alias is added to the register.
 */
export function haircutLabel(key: string): string {
  const registered = ASSET_CLASS_LABELS[key];
  if (registered) return registered;
  const words = key.toLowerCase().split('_').filter(Boolean).join(' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * The description line for an LTV threshold. The wording is the Collateral
 * Health alert vocabulary; which CHA tier each threshold maps to is not
 * asserted here.
 */
export function ltvThresholdDescription(kind: 'warning' | 'critical', ratio: number): string {
  const percent = formatRatioAsPercent(ratio);
  return kind === 'warning'
    ? `Triggers a collateral health warning at ${percent} LTV`
    : `Triggers a critical collateral health alert at ${percent} LTV`;
}

/**
 * The page description: whose configuration this is. The page reads and saves
 * one tenant's lending configuration, the tenant lendingConfigTenantId names,
 * and sets no default for any other tenant.
 */
export function lendingScopeDescription(
  tenantId: string | null,
  tenantName: string | undefined,
): string {
  const whose = !tenantId
    ? 'the session tenant'
    : tenantName
      ? tenantName
      : tenantId === APEX_TENANT_ID
        ? 'the apex tenant'
        : `tenant ${tenantId}`;
  return (
    `The lending configuration of one tenant, ${whose}: interest rates, LTV thresholds, loan limits, ` +
    'credit decision automation and asset haircuts. Saving changes this tenant only; a tenant with ' +
    'no saved configuration has none, and lending-platform answers no defaults in its place.'
  );
}

/** JSON with object keys sorted, so key order never reads as a change. */
function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`;
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return `{${Object.keys(record)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${canonicalJson(record[key])}`)
      .join(',')}}`;
  }
  return JSON.stringify(value);
}

/**
 * Whether two configurations hold the same values. lending-platform returns
 * fields and haircut classes in its own order (Postgres jsonb orders keys), so
 * a saved form compared by plain JSON.stringify would still read as changed.
 */
export function sameLendingConfiguration(a: LendingConfiguration, b: LendingConfiguration): boolean {
  return canonicalJson(a) === canonicalJson(b);
}

/**
 * The ids aria-describedby lists for a field: its description and any unit
 * drawn beside the input, so a screen reader hears "%" or "days" with the
 * number. Undefined when there is nothing to describe.
 */
export function describedByIds(ids: ReadonlyArray<string | undefined>): string | undefined {
  const present = ids.filter((id): id is string => Boolean(id));
  return present.length > 0 ? present.join(' ') : undefined;
}

// ---------------------------------------------------------------------------
// Sub-Components
// ---------------------------------------------------------------------------

function NumberField({
  id,
  label,
  description,
  value,
  onChange,
  suffix,
  prefix,
  min,
  max,
  step,
}: {
  id: string;
  label: string;
  description?: string;
  value: number | string;
  onChange: (value: string) => void;
  suffix?: string;
  prefix?: string;
  min?: number;
  max?: number;
  step?: number;
}) {
  const descriptionId = description ? `${id}-description` : undefined;
  const prefixId = prefix ? `${id}-prefix` : undefined;
  const suffixId = suffix ? `${id}-unit` : undefined;
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-sm font-sans font-medium text-primary mb-2 leading-normal"
      >
        {label}
      </label>
      <div className="flex items-center gap-2">
        {prefix && (
          <span id={prefixId} className="text-sm font-sans text-tertiary">{prefix}</span>
        )}
        <Input
          id={id}
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          min={min}
          max={max}
          step={step ?? 0.1}
          aria-describedby={describedByIds([prefixId, suffixId, descriptionId])}
          className="font-mono text-right tabular-nums"
        />
        {suffix && (
          <span id={suffixId} className="text-sm font-sans text-tertiary">{suffix}</span>
        )}
      </div>
      {description && (
        <p id={descriptionId} className="text-xs font-sans text-tertiary mt-1.5">{description}</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function LendingConfigPage() {
  const { data: config, isLoading, error } = useLendingConfig();
  const { refetch: fetchDefaults } = useLendingConfigDefaults();
  const updateMutation = useUpdateLendingConfig();
  const user = useAuthStore((state) => state.user);
  const tenantId = lendingConfigTenantId(user);
  const tenantName = user?.tenantId && user.tenantId === tenantId ? user.tenantName : undefined;
  // The tenant has no saved configuration, or one lacking fields: not a load
  // failure, a state the operator repairs by loading defaults and saving.
  const notConfigured = isLendingConfigurationMissing(error);
  const incomplete = isLendingConfigurationIncomplete(error);
  const needsSetup = notConfigured || incomplete;
  const missingFields = error instanceof LendingConfigRequestError ? error.missingFields : [];

  // Local form state -- initialized from fetched config
  const [formState, setFormState] = useState<LendingConfiguration | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Sync fetched config into local state
  useEffect(() => {
    if (config && !formState) {
      setFormState({ ...config });
    }
  }, [config, formState]);

  // Track changes. With nothing saved, loaded values are all unsaved.
  useEffect(() => {
    if (!formState) {
      setHasChanges(false);
      return;
    }
    setHasChanges(!config || !sameLendingConfiguration(config, formState));
  }, [config, formState]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const updateField = useCallback(
    <K extends keyof LendingConfiguration>(
      field: K,
      value: LendingConfiguration[K],
    ) => {
      setShowSuccess(false);
      setFormState((prev) => (prev ? { ...prev, [field]: value } : prev));
    },
    [],
  );

  const updateHaircut = useCallback((assetClass: string, value: string) => {
    setShowSuccess(false);
    setFormState((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        haircuts: {
          ...prev.haircuts,
          [assetClass]: percentInputToRatio(value) ?? 0,
        },
      };
    });
  }, []);

  const handleSave = useCallback(async () => {
    if (!formState) return;
    try {
      await updateMutation.mutateAsync(formState);
      // Stays until the next edit, and is announced: it sits in a live region.
      setShowSuccess(true);
    } catch {
      // Error state handled by mutation
    }
  }, [formState, updateMutation]);

  const handleReset = useCallback(() => {
    setShowSuccess(false);
    // With nothing saved, Reset returns to the not-configured notice.
    setFormState(config ? { ...config } : null);
  }, [config]);

  const handleLoadDefaults = useCallback(async () => {
    const result = await fetchDefaults();
    if (result.data) {
      setShowSuccess(false);
      setFormState({ ...result.data });
    }
  }, [fetchDefaults]);

  // ---------------------------------------------------------------------------
  // Loading State
  // ---------------------------------------------------------------------------

  if (isLoading) {
    return (
      <div>
        <div className="flex items-center justify-between mb-8">
          <div>
            <Skeleton className="h-8 w-64 mb-2" />
            <Skeleton className="h-4 w-96" />
          </div>
          <div className="flex gap-3">
            <Skeleton className="h-10 w-32" />
            <Skeleton className="h-10 w-32" />
          </div>
        </div>
        <div className="space-y-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}>
              <Skeleton className="h-6 w-48 mb-6" />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Error State
  // ---------------------------------------------------------------------------

  if (error && !needsSetup) {
    return (
      <div>
        <div className="bg-error/5 border border-error/20 rounded-sm p-6 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-error flex-shrink-0" aria-hidden="true" />
          <div>
            <div className="text-sm font-sans font-medium text-error">
              Failed to load lending configuration
            </div>
            <div className="text-xs font-sans text-error/80 mt-1">
              {error instanceof Error ? error.message : 'Unknown error. Verify the lending-platform service is running.'}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!formState && !needsSetup) return null;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div>
      <PageHeader
        description={lendingScopeDescription(tenantId, tenantName)}
        actions={
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="secondary"
              onClick={() => void handleLoadDefaults()}
            >
              <RotateCcw className="w-4 h-4 mr-2" aria-hidden="true" />
              Load Defaults
            </Button>
            <Button
              variant="secondary"
              onClick={handleReset}
              disabled={!hasChanges}
            >
              Reset
            </Button>
            <Button
              onClick={() => void handleSave()}
              disabled={!hasChanges || updateMutation.isPending}
            >
              <Save className="w-4 h-4 mr-2" aria-hidden="true" />
              {updateMutation.isPending ? 'Saving...' : 'Save changes'}
            </Button>
          </div>
        }
      />

      {/* Save result: a live region present before the result is inserted. */}
      <div role="status" aria-live="polite">
        {showSuccess && (
          <div className="bg-growth/5 border border-growth/20 rounded-sm p-4 mb-6 flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-growth flex-shrink-0" aria-hidden="true" />
            <div className="text-sm font-sans text-growth">
              Lending configuration saved.
            </div>
          </div>
        )}
      </div>

      {updateMutation.isError && (
        <div role="alert" className="bg-error/5 border border-error/20 rounded-sm p-4 mb-6 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-error flex-shrink-0" aria-hidden="true" />
          <div className="text-sm font-sans text-error">
            Failed to save configuration.{' '}
            {updateMutation.error instanceof Error
              ? updateMutation.error.message
              : 'Please try again.'}
          </div>
        </div>
      )}

      {!formState ? (
        incomplete ? (
          <Alert type="error" title="The saved lending configuration for this tenant lacks fields">
            {missingFields.length > 0 ? `Missing or unreadable: ${missingFields.join(', ')}. ` : ''}
            Load Defaults fills the form with Aaim&apos;s baseline values to review; Save changes
            then replaces every field.
          </Alert>
        ) : (
          <Alert type="warning" title="No lending configuration is saved for this tenant">
            Load Defaults fills the form with Aaim&apos;s baseline values to review. Nothing is
            saved for this tenant until you choose Save changes.
          </Alert>
        )
      ) : (
      <div className="space-y-6">
        <ConfigSection
          title="Interest Rates"
          intro="Base, floor, and tenant interest rate configuration"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <NumberField
              label="Base Rate"
              description="Base interest rate for playbook calculations"
              id="lending-baseRate"
              value={formState.baseRate}
              onChange={(v) => updateField('baseRate', parseFloat(v) || 0)}
              suffix="%"
              min={0}
              max={30}
              step={0.25}
            />
            <NumberField
              label="Floor Rate"
              description="Minimum allowable interest rate"
              id="lending-floorRate"
              value={formState.floorRate}
              onChange={(v) => updateField('floorRate', parseFloat(v) || 0)}
              suffix="%"
              min={0}
              max={30}
              step={0.25}
            />
            <NumberField
              label="Interest Rate"
              description="Annual interest rate applied to loans"
              id="lending-interestRate"
              value={formState.interestRate}
              onChange={(v) => updateField('interestRate', parseFloat(v) || 0)}
              suffix="%"
              min={0}
              max={30}
              step={0.25}
            />
          </div>
        </ConfigSection>

        <ConfigSection
          title="LTV Thresholds"
          intro="Loan-to-value ratio alert and action boundaries"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <NumberField
              label="Warning Threshold"
              id="lending-ltvWarningThreshold"
              description={ltvThresholdDescription('warning', formState.ltvWarningThreshold)}
              value={ratioToPercentInput(formState.ltvWarningThreshold)}
              onChange={(v) =>
                updateField('ltvWarningThreshold', percentInputToRatio(v) ?? 0)
              }
              suffix="%"
              min={0}
              max={100}
              step={1}
            />
            <NumberField
              label="Critical Threshold"
              id="lending-ltvCriticalThreshold"
              description={ltvThresholdDescription('critical', formState.ltvCriticalThreshold)}
              value={ratioToPercentInput(formState.ltvCriticalThreshold)}
              onChange={(v) =>
                updateField('ltvCriticalThreshold', percentInputToRatio(v) ?? 0)
              }
              suffix="%"
              min={0}
              max={100}
              step={1}
            />
          </div>
          {formState.ltvWarningThreshold >= formState.ltvCriticalThreshold && (
            <div className="mt-4 bg-warning/5 border border-warning/20 rounded-sm p-3 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-warning flex-shrink-0" aria-hidden="true" />
              <span className="text-xs font-sans text-warning">
                Warning threshold should be lower than critical threshold.
              </span>
            </div>
          )}
        </ConfigSection>

        <ConfigSection
          title="Loan Parameters"
          intro="Maximum amounts, terms, and credit decision automation thresholds"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <NumberField
              id="lending-maxLoanAmount"
              label="Maximum Loan Amount"
              description={`Current: ${formatMoney(formState.maxLoanAmount)}`}
              value={formState.maxLoanAmount}
              onChange={(v) =>
                updateField('maxLoanAmount', parseFloat(v) || 0)
              }
              prefix="$"
              min={0}
              step={10000}
            />
            <div>
              <NumberField
                id="lending-defaultTermDays"
                label="Default Term"
                description="Standard loan term in days"
                value={formState.defaultTermDays}
                onChange={(v) =>
                  updateField('defaultTermDays', parseInt(v, 10) || 0)
                }
                suffix="days"
                min={1}
                max={3650}
                step={30}
              />
              <div className="flex items-center gap-1.5 mt-1.5">
                <Calendar className="w-3 h-3 text-tertiary" aria-hidden="true" />
                <span className="text-xs font-sans text-tertiary">
                  {Math.round(formState.defaultTermDays / 30)} months
                </span>
              </div>
            </div>
          </div>
          <div className="border-t border-recessed pt-6">
            <Heading level={3} as={4} size="compact" className="mb-4">
              Credit Decision Automation
            </Heading>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <NumberField
                id="lending-autoApproveThreshold"
                label="Auto-Approve Threshold"
                description="Minimum credit score for automatic approval"
                value={formState.autoApproveThreshold}
                onChange={(v) =>
                  updateField(
                    'autoApproveThreshold',
                    parseFloat(v) || 0,
                  )
                }
                suffix="score"
                min={300}
                max={850}
                step={10}
              />
              <NumberField
                id="lending-autoDenyThreshold"
                label="Auto-Deny Threshold"
                description="Maximum credit score for automatic denial"
                value={formState.autoDenyThreshold}
                onChange={(v) =>
                  updateField('autoDenyThreshold', parseFloat(v) || 0)
                }
                suffix="score"
                min={300}
                max={850}
                step={10}
              />
            </div>
            {formState.autoDenyThreshold >= formState.autoApproveThreshold && (
              <div className="mt-4 bg-warning/5 border border-warning/20 rounded-sm p-3 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-warning flex-shrink-0" aria-hidden="true" />
                <span className="text-xs font-sans text-warning">
                  Auto-deny threshold should be lower than auto-approve threshold.
                  Scores between these values require manual review.
                </span>
              </div>
            )}
          </div>
        </ConfigSection>

        <ConfigSection
          title="Asset Haircuts"
          intro="Collateral discount percentages by asset class. A higher haircut is a more conservative valuation."
        >
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {Object.entries(formState.haircuts)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([assetClass, haircut]) => (
                <div key={assetClass}>
                  <label
                    htmlFor={`haircut-${assetClass}`}
                    className="block text-sm font-sans font-medium text-primary mb-2 leading-normal"
                  >
                    {haircutLabel(assetClass)}
                  </label>
                  <div className="flex items-center gap-2">
                    <Input
                      id={`haircut-${assetClass}`}
                      type="number"
                      value={ratioToPercentInput(haircut)}
                      onChange={(e) =>
                        updateHaircut(assetClass, e.target.value)
                      }
                      min={0}
                      max={100}
                      step={1}
                      aria-describedby={`haircut-${assetClass}-unit`}
                      className="font-mono text-right tabular-nums"
                    />
                    <span id={`haircut-${assetClass}-unit`} className="text-sm font-sans text-tertiary">%</span>
                  </div>
                  <div className="mt-1.5" aria-hidden="true">
                    <div className="w-full h-1.5 bg-recessed rounded-full overflow-hidden">
                      <div
                        className="h-full bg-neutral-data rounded-full transition-all"
                        style={{ width: `${Math.min(haircut * 100, 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
          </div>
          <div className="mt-6 bg-elevated rounded-sm p-4">
            <DollarSign className="w-4 h-4 text-tertiary inline-block mr-1.5" aria-hidden="true" />
            <span className="text-xs font-sans text-secondary">
              Haircut values represent the discount applied to collateral market value.
              A 25% haircut on equities means $100,000 in equities provides $75,000
              in pledging capacity.
            </span>
          </div>
        </ConfigSection>
      </div>
      )}
    </div>
  );
}
